import React, { useState, useEffect, useCallback } from 'react'
import { PageTemplate } from '@/components/templates'
import { CONTAINER_SIZES } from '@/constants'
import PartnerReferralsStatsComponent from '@/components/molecules/PartnerReferralsStats'
import PartnerReferralsTable from '@/components/molecules/PartnerReferralsTable'
import { ReferralFilters } from '@/components/filters'
import { Button } from '@/components/ui/button'
import { RefreshCw, Download } from 'lucide-react'
import { 
  PartnerReferralsFilters, 
  PartnerReferralsStatsFilters,
  PartnerReferral,
  PartnerReferralsStats,
  ApiKey
} from '@/types/partner'
import { partnerService } from '@/services/partnerService'
import { exportReferralsToCSV } from '@/utils/export'

const PartnerReferralsScreen: React.FC = () => {
  // State for referrals data
  const [referrals, setReferrals] = useState<PartnerReferral[]>([])
  const [stats, setStats] = useState<PartnerReferralsStats | null>(null)
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
  
  // Loading states
  const [loadingReferrals, setLoadingReferrals] = useState(false)
  const [loadingStats, setLoadingStats] = useState(false)
  
  // Pagination
  const [total, setTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(20)
  
  // Sorting
  const [sortField, setSortField] = useState('date')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  
  // Filters
  const [filters, setFilters] = useState<PartnerReferralsFilters>({
    limit: pageSize,
    offset: 0,
    project_name: '',
    status: undefined,
    type: undefined,
    referral_type: undefined,
    api_key_id: '',
    date_from: '',
    date_to: '',
    min_tranche_size: undefined,
    max_tranche_size: undefined
  })

  // Load API keys
  const loadApiKeys = useCallback(async () => {
    try {
      const response = await partnerService.listApiKeys()
      setApiKeys(response.data)
    } catch (error) {
      console.error('Failed to load API keys:', error)
    }
  }, [])

  // Load referrals data
  const loadReferrals = useCallback(async () => {
    try {
      setLoadingReferrals(true)
      const response = await partnerService.getPartnerReferrals({
        ...filters,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize
      })
      setReferrals(response.data)
      setTotal(response.total)
    } catch (error) {
      console.error('Failed to load referrals:', error)
    } finally {
      setLoadingReferrals(false)
    }
  }, [filters, currentPage, pageSize])

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      setLoadingStats(true)
      const statsFilters: PartnerReferralsStatsFilters = {
        api_key_id: filters.api_key_id,
        date_from: filters.date_from,
        date_to: filters.date_to
      }
      const statsData = await partnerService.getPartnerReferralsStats(statsFilters)
      setStats(statsData)
    } catch (error) {
      console.error('Failed to load stats:', error)
    } finally {
      setLoadingStats(false)
    }
  }, [filters.api_key_id, filters.date_from, filters.date_to])

  // Handle filter changes
  const handleFiltersChange = (newFilters: Partial<PartnerReferralsFilters>) => {
    const updatedFilters = { ...filters, ...newFilters }
    setFilters(updatedFilters)
    setCurrentPage(1) // Reset to first page when filters change
  }

  // Handle sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      limit: pageSize,
      offset: 0,
      project_name: '',
      status: undefined,
      type: undefined,
      referral_type: undefined,
      api_key_id: '',
      date_from: '',
      date_to: '',
      min_tranche_size: undefined,
      max_tranche_size: undefined
    })
    setCurrentPage(1)
  }

  // Export data
  const handleExport = () => {
    if (referrals.length > 0) {
      const timestamp = new Date().toISOString().split('T')[0]
      exportReferralsToCSV(referrals, `partner-referrals-${timestamp}.csv`)
    }
  }

  // Refresh data
  const handleRefresh = () => {
    loadReferrals()
    loadStats()
  }

  // Load data on mount and when dependencies change
  useEffect(() => {
    loadApiKeys()
  }, [loadApiKeys])

  useEffect(() => {
    loadReferrals()
  }, [loadReferrals])

  useEffect(() => {
    loadStats()
  }, [loadStats])

  return (
    <PageTemplate
      title="Partner Referrals"
      subtitle="Track and manage your inquiries"
      maxWidth={CONTAINER_SIZES.WIDE}
    >
      <div className="space-y-6">
        {/* Header Actions */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Partner Referrals</h1>
            <p className="text-muted-foreground">
              Manage and track all referrals submitted through your API keys
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={loadingReferrals || loadingStats}
            >
              <RefreshCw size={16} className="mr-2" />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={loadingReferrals}
            >
              <Download size={16} className="mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Statistics */}
        <PartnerReferralsStatsComponent
          stats={stats}
          loading={loadingStats}
        />

        {/* Filters */}
        <ReferralFilters
          search={filters.project_name || ''}
          onSearchChange={(value) => handleFiltersChange({ project_name: value })}
          status={filters.status || ''}
          onStatusChange={(value) => handleFiltersChange({ 
            status: value ? (value as 'PENDING' | 'APPROVED' | 'REJECTED' | 'CONTACTED' | 'CANCELLED' | 'COMPLETED') : undefined 
          })}
          type={filters.type || ''}
          onTypeChange={(value) => handleFiltersChange({ 
            type: value ? (value as 'INQUIRY' | 'RFQ') : undefined 
          })}
          referralType={filters.referral_type || ''}
          onReferralTypeChange={(value) => handleFiltersChange({ 
            referral_type: value ? (value as 'BUY' | 'SELL') : undefined 
          })}
          apiKeyId={filters.api_key_id || ''}
          onApiKeyChange={(value) => handleFiltersChange({ api_key_id: value })}
          dateFrom={filters.date_from || ''}
          onDateFromChange={(value) => handleFiltersChange({ date_from: value })}
          dateTo={filters.date_to || ''}
          onDateToChange={(value) => handleFiltersChange({ date_to: value })}
          minTrancheSize={filters.min_tranche_size}
          onMinTrancheSizeChange={(value) => handleFiltersChange({ min_tranche_size: value })}
          maxTrancheSize={filters.max_tranche_size}
          onMaxTrancheSizeChange={(value) => handleFiltersChange({ max_tranche_size: value })}
          onReset={handleResetFilters}
          apiKeys={apiKeys}
        />

        {/* Table */}
        <PartnerReferralsTable
          referrals={referrals}
          loading={loadingReferrals}
          onSort={handleSort}
          sortField={sortField}
          sortDirection={sortDirection}
        />

        {/* Pagination */}
        {total > pageSize && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, total)} of {total} referrals
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-sm">
                Page {currentPage} of {Math.ceil(total / pageSize)}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= Math.ceil(total / pageSize)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </PageTemplate>
  )
}

export default PartnerReferralsScreen
